import { useEffect, useRef, useState } from 'react';
import type { GameSocketApi } from './hooks/useGameSocket';
import { useGameStore, useRoomCode, useSnapshot } from './store/gameStore';
import { MAX_PLAYERS, TURN_TIME_OPTIONS } from '../../shared/types';
import { COLOR_LABELS } from '../../shared/board';
import type { Color } from '../../shared/board';
import { ensureSession } from './auth/session';

export default function Lobby({ socket }: { socket: GameSocketApi }) {
  const connected = useGameStore((s) => s.connected);
  const myId = useGameStore((s) => s.myPlayerId);
  const snapshot = useSnapshot();
  const roomCode = useRoomCode();
  const error = useGameStore((s) => s.error);

  const [name, setName] = useState(loadName());
  const [joinCode, setJoinCode] = useState('');
  const nameEditedRef = useRef(false);
  const updateName = (value: string) => {
    nameEditedRef.current = true;
    setName(value);
  };

  useEffect(() => {
    let active = true;
    void ensureSession().then((session) => {
      if (!active || !session?.name || nameEditedRef.current) return;
      setName(session.name.slice(0, 24));
    });
    return () => {
      active = false;
    };
  }, []);

  const inRoom = !!snapshot && !!myId && snapshot.players.some((p) => p.id === myId);

  if (!inRoom) {
    return <Home name={name} setName={updateName} joinCode={joinCode} setJoinCode={setJoinCode} socket={socket} connected={connected} error={error} />;
  }

  return <RoomLobby socket={socket} roomCode={roomCode} snapshot={snapshot!} myId={myId!} error={error} />;
}

// ─── Shared atmosphere ───────────────────────────────────────────────────────

/** Vibrant page backdrop: deep violet + colourful glow orbs + faint dot grid. */
function ArenaBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
        }}
      />
      <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#ff5a5a]/20 blur-3xl lud-float-slow" />
      <div className="absolute top-1/4 -right-28 h-96 w-96 rounded-full bg-[#4aa8ff]/20 blur-3xl" />
      <div className="absolute bottom-0 -left-20 h-96 w-96 rounded-full bg-[#35d07f]/15 blur-3xl" />
      <div className="absolute -bottom-24 right-1/4 h-80 w-80 rounded-full bg-[#ffce3d]/15 blur-3xl lud-float" />
      <div className="absolute top-10 left-1/3 h-64 w-64 rounded-full bg-[#ff8a3d]/15 blur-3xl" />
    </div>
  );
}

function Marble({ color, className = '' }: { color: string; className?: string }) {
  return (
    <span
      className={`inline-block h-8 w-8 rounded-full border-2 border-white/40 shadow-lg ${className}`}
      style={{
        background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, ${color} 45%, rgba(0,0,0,0.25) 130%)`,
        boxShadow: `0 8px 18px -6px ${color}88, inset 0 -4px 6px rgba(0,0,0,0.25)`,
      }}
    />
  );
}

const MARBLES: Record<Color, string> = {
  red: '#ff5a5a',
  green: '#35d07f',
  yellow: '#ffce3d',
  blue: '#4aa8ff',
};

// ─── Home / landing ──────────────────────────────────────────────────────────

function Home({
  name,
  setName,
  joinCode,
  setJoinCode,
  socket,
  connected,
  error,
}: {
  name: string;
  setName: (v: string) => void;
  joinCode: string;
  setJoinCode: (v: string) => void;
  socket: GameSocketApi;
  connected: boolean;
  error: string | null;
}) {
  const [dice, setDice] = useState(0);
  const start = () => {
    if (!name.trim() || !connected) return;
    saveName(name.trim());
    socket.createRoom(name.trim());
  };
  const join = () => {
    if (!name.trim() || joinCode.trim().length < 6 || !connected) return;
    saveName(name.trim());
    socket.joinRoom(joinCode.trim().toUpperCase(), name.trim());
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#1a1240] via-lud-bg to-[#170f33]">
      <ArenaBackdrop />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center px-6 py-12">
        {/* Hero */}
        <div className="mb-9 text-center">
          <div className="flex items-center justify-center gap-3">
            <Marble color={MARBLES.red} className="lud-float" />
            <Marble color={MARBLES.green} className="lud-float-slow -translate-y-1" />
            <div className="mx-1 flex h-16 w-16 -rotate-6 items-center justify-center rounded-2xl bg-gradient-to-br from-[#ffd257] via-[#ffb03d] to-[#ff5f16] shadow-glow-gold">
              <span className="text-3xl leading-none drop-shadow-[0_3px_6px_rgba(0,0,0,0.35)]">🎲</span>
            </div>
            <Marble color={MARBLES.yellow} className="lud-float-slow -translate-y-2" />
            <Marble color={MARBLES.blue} className="lud-float" />
          </div>

          <h1
            className="mt-6 bg-gradient-to-br from-[#ffe9a8] via-[#ffc94d] to-[#ff8a3d] bg-clip-text font-display text-[4rem] font-bold leading-none tracking-tight text-transparent drop-shadow-[0_8px_24px_rgba(255,138,61,0.35)]"
            style={{ filter: 'saturate(1.05)' }}
          >
            Ludo
          </h1>
          <p className="mt-2 text-sm font-bold uppercase tracking-[0.24em] text-[#ffc96b]">
            Roll · move · capture
          </p>
          <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-lud-muted">
            Race your four tokens around the board and into the centre before everyone
            else. Create a room, grab your friends, and roll your way to victory.
          </p>
        </div>

        {/* Action card */}
        <div className="lud-glass w-full rounded-3xl p-7">
          {error && (
            <div className="mb-4 rounded-xl border border-red-400/30 bg-red-500/15 px-4 py-2.5 text-sm font-semibold text-red-200">
              {error}
            </div>
          )}
          {!connected && (
            <div className="mb-4 rounded-xl border border-amber-300/30 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-200">
              Connecting to server…
            </div>
          )}

          <label className="block text-xs font-bold uppercase tracking-widest text-lud-muted">
            Your name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 24))}
            maxLength={24}
            placeholder="Drop a nickname"
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.07] px-4 py-3 text-sm font-medium text-lud-ink outline-none transition placeholder:text-lud-muted/60 focus:border-lud-accent/60 focus:bg-white/[0.1] focus:ring-2 focus:ring-lud-accent/20"
          />

          <button
            onClick={start}
            disabled={!connected || !name.trim()}
            className="lud-gradient mt-4 w-full rounded-xl bg-gradient-to-r from-[#ffd257] via-[#ff8a3d] to-[#ff5f16] py-3.5 text-sm font-bold tracking-wide text-white shadow-glow transition hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-35 disabled:saturate-0"
          >
            Create game room
          </button>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-lud-muted/70">
              or join
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="flex gap-2">
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="CODE"
              className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.07] px-4 py-3 text-center font-mono text-lg font-bold tracking-[0.35em] text-lud-ink uppercase outline-none transition placeholder:text-lud-muted/50 focus:border-lud-accent/60 focus:bg-white/[0.1] focus:ring-2 focus:ring-lud-accent/20"
            />
            <button
              onClick={join}
              disabled={!connected || !name.trim() || joinCode.trim().length < 6}
              className="rounded-xl bg-gradient-to-br from-white/15 to-white/5 px-7 py-3 text-sm font-bold text-lud-ink ring-1 ring-white/15 transition hover:bg-white/20 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Join
            </button>
          </div>
        </div>

        {/* Quick how-to chips */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
          {[
            { icon: '🎲', text: 'Roll a 6 to launch' },
            { icon: '⚔️', text: 'Capture rivals' },
            { icon: '⭐', text: 'Safe squares protect' },
            { icon: '🏆', text: 'All 4 home to win' },
          ].map((chip) => (
            <span
              key={chip.text}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-lud-muted"
            >
              <span>{chip.icon}</span>
              {chip.text}
            </span>
          ))}
        </div>

        {/* Playful dice countdown */}
        <div className="mt-7 flex items-center justify-center gap-2.5" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              type="button"
              onMouseEnter={() => setDice(v + 1)}
              onMouseLeave={() => setDice(0)}
              className={`flex h-10 w-10 items-center justify-center rounded-xl border text-sm font-bold transition-all duration-200 ${
                dice === v + 1
                  ? '-translate-y-1.5 border-[#ffce3d] bg-[#ffce3d]/20 text-[#ffe58a] shadow-[0_0_18px_rgba(255,206,61,0.35)]'
                  : 'border-white/10 bg-white/[0.04] text-lud-muted/70'
              }`}
            >
              {v + 1}
            </button>
          ))}
        </div>
        <p className="mt-6 text-center text-[11px] font-medium text-lud-muted/70">
          Free · No downloads · Up to {MAX_PLAYERS} players per room
        </p>
      </div>
    </div>
  );
}

// ─── Waiting room ────────────────────────────────────────────────────────────

function RoomLobby({
  socket,
  roomCode,
  snapshot,
  myId,
  error,
}: {
  socket: GameSocketApi;
  roomCode: string | null;
  snapshot: NonNullable<ReturnType<typeof useSnapshot>>;
  myId: string;
  error: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const [botDifficulty, setBotDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const me = snapshot.players.find((p) => p.id === myId)!;
  const isHost = snapshot.hostId === myId;
  const readyCount = snapshot.players.filter((p) => p.ready).length;
  const allReady = snapshot.players.length >= 2 && snapshot.players.every((p) => p.ready);
  const roomFull = snapshot.players.length >= MAX_PLAYERS;

  const copyCode = async () => {
    if (!roomCode) return;
    try {
      await navigator.clipboard.writeText(roomCode);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = roomCode;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#1a1240] via-lud-bg to-[#170f33]">
      <ArenaBackdrop />

      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-12">
        <div className="text-center">
          {/* Room code pill */}
          <span className="lud-glass inline-flex items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold text-lud-ink">
            <span className="text-[10px] font-bold uppercase tracking-widest text-lud-muted">
              Room
            </span>
            <button
              onClick={copyCode}
              title="Copy room code"
              className="rounded-lg bg-gradient-to-br from-[#ffd257] to-[#ff8a3d] px-2.5 py-0.5 font-mono text-base font-bold tracking-[0.2em] text-white shadow-glow-gold hover:brightness-110"
            >
              {roomCode}
            </button>
            <span className="text-xs font-medium text-lud-muted">
              {copied ? 'Copied!' : 'tap to copy'}
            </span>
          </span>

          <h1 className="mt-6 bg-gradient-to-br from-white via-[#ffd9a0] to-[#ff9a4a] bg-clip-text font-display text-4xl font-bold tracking-tight text-transparent">
            Waiting room
          </h1>
          <p className="mt-2 text-sm font-semibold text-lud-muted">
            {snapshot.players.length}/{MAX_PLAYERS} players · {readyCount}/{snapshot.players.length} ready
          </p>
        </div>

        {/* Single lobby card — every control lives here at one uniform size */}
        <div className="lud-glass mt-8 rounded-3xl p-5 sm:p-6">
          <p className="px-1 text-xs font-bold uppercase tracking-widest text-lud-muted">
            Players
          </p>

          {/* Uniform seats: equal-width grid, equal height cards */}
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {snapshot.players.map((p) => {
              const accent = MARBLES[p.color];
              const isBot = !!p.isBot;
              return (
                <div
                  key={p.id}
                  className={`flex min-h-[68px] items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 transition ${
                    p.ready ? 'ring-1 ring-[#ffce3d]/50' : ''
                  }`}
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-white/40 text-sm font-bold text-white shadow-lg"
                    style={{
                      background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, ${accent} 45%, rgba(0,0,0,0.3) 130%)`,
                      boxShadow: `0 10px 24px -8px ${accent}aa`,
                    }}
                  >
                    {isBot ? '🤖' : p.name.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-bold text-lud-ink">
                      {p.name}
                      {p.id === snapshot.hostId && (
                        <span className="ml-1.5 rounded-full bg-[#ffc94d]/20 px-2 py-0.5 text-[10px] font-bold uppercase text-[#ffd98a]">
                          👑 host
                        </span>
                      )}
                      {isBot && (
                        <span className="ml-1.5 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase text-lud-muted">
                          bot
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-lud-muted">
                      {COLOR_LABELS[p.color]} {p.id === myId && '· you'} {!p.connected && !isBot && '· disconnected'}
                    </p>
                    {isBot &&
                      (isHost ? (
                        <select
                          value={p.botDifficulty ?? 'medium'}
                          onChange={(e) =>
                            socket.setBotDifficulty(p.id, e.target.value as 'easy' | 'medium' | 'hard')
                          }
                          aria-label={`Bot difficulty for ${p.name}`}
                          className="mt-1.5 h-8 cursor-pointer rounded-lg border border-white/10 bg-black/20 px-2 text-xs font-bold text-lud-ink outline-none transition hover:bg-black/30 focus:ring-2 focus:ring-[#ffc94d]/50"
                        >
                          <option value="easy" className="bg-[#241a4d]">
                            Easy
                          </option>
                          <option value="medium" className="bg-[#241a4d]">
                            Medium
                          </option>
                          <option value="hard" className="bg-[#241a4d]">
                            Hard
                          </option>
                        </select>
                      ) : (
                        <span className="mt-1.5 inline-block rounded-full bg-white/5 px-2 py-0.5 text-[11px] font-bold capitalize text-lud-muted">
                          {p.botDifficulty ?? 'medium'}
                        </span>
                      ))}
                  </div>
                  {isBot && isHost ? (
                    <button
                      onClick={() => socket.removeBot(p.id)}
                      title={`Remove ${p.name}`}
                      aria-label={`Remove ${p.name}`}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] text-sm text-lud-muted transition hover:border-red-400/40 hover:text-red-300"
                    >
                      ✕
                    </button>
                  ) : (
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
                        p.ready
                          ? 'bg-[#ffce3d]/20 text-[#ffe58a]'
                          : 'bg-white/5 text-lud-muted/70'
                      }`}
                    >
                      {p.ready ? 'Ready' : 'Waiting'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Settings rows — uniform h-12 controls in boxed rows */}
          <p className="mt-5 px-1 text-xs font-bold uppercase tracking-widest text-lud-muted">
            Game setup
          </p>
          <div className="mx-auto mt-3 grid max-w-xs gap-2.5">
            {isHost &&
              (roomFull ? (
                <div className="flex h-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-4 text-xs font-medium text-lud-muted/80">
                  Room is full — remove a bot to change the lineup.
                </div>
              ) : (
                <div className="flex h-11 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2 pl-3">
                  <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-lud-muted">
                    🤖 Add bot
                  </span>
                  <select
                    value={botDifficulty}
                    onChange={(e) => setBotDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                    aria-label="Bot difficulty"
                    className="h-9 min-w-0 flex-1 cursor-pointer rounded-lg border border-white/10 bg-black/20 px-2.5 text-sm font-bold text-lud-ink outline-none transition hover:bg-black/30 focus:ring-2 focus:ring-[#ffc94d]/50"
                  >
                    <option value="easy" className="bg-[#241a4d]">
                      Easy
                    </option>
                    <option value="medium" className="bg-[#241a4d]">
                      Medium
                    </option>
                    <option value="hard" className="bg-[#241a4d]">
                      Hard
                    </option>
                  </select>
                  <button
                    onClick={() => socket.addBot(botDifficulty)}
                    className="h-9 shrink-0 rounded-lg bg-gradient-to-br from-white/20 to-white/5 px-5 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15 active:scale-[0.98]"
                  >
                    Add
                  </button>
                </div>
              ))}
            {isHost && snapshot.players.length === 1 && !roomFull && (
              <p className="text-center text-xs font-medium text-lud-muted/80">
                Playing solo? Add 1–3 bots to start instantly — no friends needed.
              </p>
            )}

            {isHost && (
              <label className="flex h-11 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3">
                <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-lud-muted">
                  Turn timer
                </span>
                <select
                  value={snapshot.turnTimeoutMs / 1000}
                  onChange={(e) => socket.setTimeLimit(Number(e.target.value))}
                  className="h-9 min-w-0 flex-1 cursor-pointer rounded-lg border border-white/10 bg-black/20 px-2.5 text-sm font-bold text-lud-ink outline-none transition hover:bg-black/30 focus:ring-2 focus:ring-[#ffc94d]/50"
                >
                  {TURN_TIME_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-[#241a4d] text-lud-ink">
                      {s}s
                    </option>
                  ))}
                </select>
                <span className="shrink-0 text-xs font-medium text-lud-muted">per turn</span>
              </label>
            )}
          </div>

          {error && (
            <div className="mt-3 rounded-xl border border-red-400/30 bg-red-500/15 px-4 py-2.5 text-sm font-semibold text-red-200">
              {error}
            </div>
          )}

          {/* Actions — uniform h-12 buttons, equal widths */}
          <div className={`mt-5 grid gap-2.5 ${isHost ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            <button
              onClick={() => socket.setReady(!me.ready)}
              className={`h-12 w-full rounded-xl text-sm font-bold tracking-wide transition active:scale-[0.98] ${
                me.ready
                  ? 'bg-gradient-to-br from-[#ffd257] to-[#ffb03d] text-[#3a2400] shadow-glow-gold hover:brightness-110'
                  : 'lud-gradient bg-gradient-to-r from-[#ffd257] via-[#ff8a3d] to-[#ff5f16] text-white shadow-glow hover:brightness-110'
              }`}
            >
              {me.ready ? 'Unready' : "I'm ready"}
            </button>
            {isHost && (
              <button
                onClick={() => socket.startGame()}
                disabled={!allReady}
                className="h-12 w-full rounded-xl bg-gradient-to-br from-white/20 to-white/5 text-sm font-bold tracking-wide text-white ring-1 ring-white/20 transition hover:bg-white/15 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-35"
              >
                Start game
              </button>
            )}
            <button
              onClick={() => socket.leave()}
              className="h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] text-sm font-bold text-lud-muted transition hover:border-red-400/40 hover:text-red-300"
            >
              Leave
            </button>
          </div>

          {!allReady && snapshot.players.length >= 2 && (
            <p className="mt-3 text-center text-xs font-medium text-lud-muted/80">
              Everyone needs to hit <span className="font-bold text-[#ffe58a]">“I'm ready”</span> to start.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function loadName(): string {
  try {
    return window.localStorage.getItem('ludo-name') || '';
  } catch {
    return '';
  }
}
function saveName(name: string): void {
  try {
    window.localStorage.setItem('ludo-name', name);
  } catch {
    /* ignore */
  }
}